<template>
  <footer class="border-t border-t1-border py-12 md:py-16" role="contentinfo">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col items-center gap-6">
      <div class="flex items-center gap-2 opacity-50 grayscale">
        <button
          v-if="canOpenOwnerLogoModal"
          type="button"
          class="min-h-[44px] min-w-[44px] shrink-0 inline-flex items-center justify-center overflow-hidden rounded border-0 bg-transparent p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 opacity-100"
          :aria-label="$t('public.ownerEditLogoAria')"
          @click="openOwnerLogoModal"
        >
          <span v-if="logoUrl" class="size-8 overflow-hidden rounded inline-block">
            <img :src="logoUrl" :alt="restaurantName" class="w-full h-full object-contain" />
          </span>
          <span v-else class="size-8 shrink-0 inline-flex items-center justify-center text-t1-neutral-dark">
            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" class="size-8" aria-hidden="true">
              <path d="M44 4H30.6666V17.3334H17.3334V30.6666H4V44H44V4Z" fill="currentColor" />
            </svg>
          </span>
        </button>
        <template v-else>
          <div v-if="logoUrl" class="size-8 shrink-0 overflow-hidden rounded">
            <img :src="logoUrl" :alt="restaurantName" class="w-full h-full object-contain" />
          </div>
          <div v-else class="size-8 shrink-0">
            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path d="M44 4H30.6666V17.3334H17.3334V30.6666H4V44H44V4Z" fill="currentColor" />
            </svg>
          </div>
        </template>
        <div class="block w-full text-center text-sm font-bold uppercase tracking-tight text-t1-neutral-dark">{{ restaurantName || 'Restaurant' }}</div>
      </div>
      <div class="flex flex-wrap items-center justify-center gap-4 min-h-[44px]">
        <button
          type="button"
          class="text-t1-neutral-muted hover:text-t1-neutral-dark text-sm font-medium min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          @click="openLegalModal('terms')"
        >
          {{ $t('app.termsOfService') }}
        </button>
        <button
          type="button"
          class="text-t1-neutral-muted hover:text-t1-neutral-dark text-sm font-medium min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          @click="openLegalModal('privacy')"
        >
          {{ $t('app.privacyPolicy') }}
        </button>
        <button
          type="button"
          class="text-t1-neutral-muted hover:text-t1-neutral-dark text-sm font-medium min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          @click="openCookiePrefs"
        >
          {{ $t('landing.footer.cookies') }}
        </button>
      </div>
      <div class="text-center text-t1-neutral-muted text-sm space-y-2">
        <p>© {{ currentYear }} {{ restaurantName || 'Restaurant' }}. {{ $t('public.allRightsReserved') }}</p>
      </div>
      <PublicFooterCredits v-if="showCredits" class="mt-2" compact />
    </div>
    <LegalContentModal
      :open="legalModalOpen"
      :type="legalModalType"
      :locale="modalLocale"
      @close="closeLegalModal"
    />
  </footer>
</template>

<script setup>
import { ref, computed } from 'vue'
import LegalContentModal from '@/components/legal/LegalContentModal.vue'
import PublicFooterCredits from '@/components/public/PublicFooterCredits.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'
import { useCookieConsentStore } from '@/stores/sitePrefs'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()
const cookieConsentStore = useCookieConsentStore()

defineProps({
  restaurantName: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  /** When true, show "Site created via QRestoo" / DLR credits block. */
  showCredits: { type: Boolean, default: true },
  /** Locale for legal modal content (e.g. restaurant default_locale). */
  modalLocale: { type: String, default: 'en' },
})

const legalModalOpen = ref(false)
const legalModalType = ref('terms')

const currentYear = computed(() => new Date().getFullYear())

function openLegalModal(type) {
  legalModalType.value = type
  legalModalOpen.value = true
}

function closeLegalModal() {
  legalModalOpen.value = false
}

function openCookiePrefs() {
  cookieConsentStore.openPrefs()
}
</script>
